import axios, { AxiosInstance } from 'axios'

// PostgREST operators
export const operators = {
  eq: 'eq',        // equals
  neq: 'neq',      // not equals
  gt: 'gt',        // greater than
  gte: 'gte',      // greater than or equal
  lt: 'lt',        // less than
  lte: 'lte',      // less than or equal
  like: 'like',    // case-sensitive pattern match
  ilike: 'ilike',  // case-insensitive pattern match
  in: 'in',        // one of a list of values
  is: 'is',        // IS NULL, IS TRUE, IS FALSE
  not: 'not',      // negation
  cs: 'cs',        // contains (arrays)
  cd: 'cd',        // contained in (arrays)
  ov: 'ov',        // overlaps (arrays)
  sl: 'sl',        // strictly left of (ranges)
  sr: 'sr',        // strictly right of (ranges)
  nxr: 'nxr',      // not extends to the right (ranges)
  nxl: 'nxl',      // not extends to the left (ranges)
  adj: 'adj',      // adjacent (ranges)
} as const

export type Operator = keyof typeof operators

export interface Filter {
  column: string
  operator: Operator
  value: any
}

export interface QueryOptions {
  select?: string
  order?: string | string[]
  limit?: number
  offset?: number
  filters?: Filter[]
  or?: Filter[][]  // For OR conditions
  count?: 'exact' | 'planned' | 'estimated'
}

// Create axios instance with default config for IntelliJetNet API
const intelliJetNetAPI: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_INTELLIJETNET_API_URL,
  headers: {
    'apikey': process.env.REACT_APP_INTELLIJETNET_ANON_KEY!,
    'Authorization': `Bearer ${process.env.REACT_APP_INTELLIJETNET_ANON_KEY!}`,
    'Content-Type': 'application/json',
    'Prefer': 'return=representation'
  }
})

// Add request interceptor
intelliJetNetAPI.interceptors.request.use((config) => {
  return config
}, (error) => {
  return Promise.reject(error)
})

// Add response interceptor for error handling
intelliJetNetAPI.interceptors.response.use((response) => {
  return response
}, (error) => {
  return Promise.reject(error)
})

// Build query string from filters
function buildQueryString(options: QueryOptions): string {
  const params = new URLSearchParams()
  
  // Add filters
  if (options.filters) {
    options.filters.forEach(filter => {
      if (filter.value !== undefined && filter.value !== null) {
        if (filter.operator === 'in') {
          params.append(filter.column, `${filter.operator}.(${Array.isArray(filter.value) ? filter.value.join(',') : filter.value})`)
        } else if (filter.operator === 'is') {
          params.append(filter.column, `${filter.operator}.${filter.value}`)
        } else {
          params.append(filter.column, `${filter.operator}.${filter.value}`)
        }
      }
    })
  }
  
  // Add OR conditions
  if (options.or && options.or.length > 0) {
    const orConditions = options.or.map(group => {
      return `(${group.map(f => `${f.column}.${f.operator}.${f.value}`).join(',')})`
    }).join(',')
    params.append('or', orConditions)
  }
  
  // Add other options
  if (options.select) params.append('select', options.select)
  if (options.order) {
    const orderStr = Array.isArray(options.order) ? options.order.join(',') : options.order
    params.append('order', orderStr)
  }
  if (options.limit !== undefined) params.append('limit', options.limit.toString())
  if (options.offset !== undefined) params.append('offset', options.offset.toString())
  
  return params.toString()
}

// Query builder class for more complex queries
export class QueryBuilder {
  private options: QueryOptions = {}
  
  constructor(private table: string) {}
  
  select(columns: string | string[]): this {
    this.options.select = Array.isArray(columns) ? columns.join(',') : columns
    return this
  }
  
  filter(column: string, operator: Operator, value: any): this {
    if (!this.options.filters) this.options.filters = []
    this.options.filters.push({ column, operator, value })
    return this
  }
  
  eq(column: string, value: any): this {
    return this.filter(column, 'eq', value)
  }
  
  neq(column: string, value: any): this {
    return this.filter(column, 'neq', value)
  }
  
  gt(column: string, value: any): this {
    return this.filter(column, 'gt', value)
  }
  
  gte(column: string, value: any): this {
    return this.filter(column, 'gte', value)
  }
  
  lt(column: string, value: any): this {
    return this.filter(column, 'lt', value)
  }
  
  lte(column: string, value: any): this {
    return this.filter(column, 'lte', value)
  }
  
  like(column: string, pattern: string): this {
    return this.filter(column, 'like', pattern)
  }
  
  ilike(column: string, pattern: string): this {
    return this.filter(column, 'ilike', pattern)
  }
  
  in(column: string, values: any[]): this {
    return this.filter(column, 'in', values)
  }
  
  is(column: string, value: 'null' | 'true' | 'false'): this {
    return this.filter(column, 'is', value)
  }
  
  contains(column: string, value: any[]): this {
    return this.filter(column, 'cs', `{${value.join(',')}}`)
  }
  
  or(...conditions: Filter[][]): this {
    this.options.or = conditions
    return this
  }
  
  order(column: string, ascending: boolean = true, nullsFirst: boolean = false): this {
    const orderStr = `${column}.${ascending ? 'asc' : 'desc'}${nullsFirst ? '.nullsfirst' : ''}`
    if (!this.options.order) {
      this.options.order = orderStr
    } else if (Array.isArray(this.options.order)) {
      this.options.order.push(orderStr)
    } else {
      this.options.order = [this.options.order, orderStr]
    }
    return this
  }
  
  limit(limit: number): this {
    this.options.limit = limit
    return this
  }
  
  offset(offset: number): this {
    this.options.offset = offset
    return this
  }
  
  count(type: 'exact' | 'planned' | 'estimated' = 'exact'): this {
    this.options.count = type
    return this
  }
  
  async execute<T>(): Promise<{ data: T[], count?: number }> {
    const queryString = buildQueryString(this.options)
    const headers: any = {}
    
    if (this.options.count) {
      headers['Prefer'] = `count=${this.options.count}`
    }
    
    const response = await intelliJetNetAPI.get<T[]>(`/${this.table}?${queryString}`, { headers })
    
    const result: { data: T[], count?: number } = { data: response.data }
    
    if (this.options.count && response.headers['content-range']) {
      const match = response.headers['content-range'].match(/\d+$/);
      if (match) result.count = parseInt(match[0])
    }
    
    return result
  }
}

// Main service object
export const intelliJetNetService = {
  // Create a query builder
  from(table: string): QueryBuilder {
    return new QueryBuilder(table)
  },

  // Simple query with options
  async query<T>(table: string, options: QueryOptions): Promise<T[]> {
    const queryString = buildQueryString(options)
    const response = await intelliJetNetAPI.get<T[]>(`/${table}?${queryString}`)
    return response.data
  },

  // Get a single record by ID
  async getById<T>(table: string, id: string | number, select?: string): Promise<T> {
    const builder = new QueryBuilder(table).eq('id', id)
    if (select) builder.select(select)
    const { data } = await builder.execute<T>()
    return data[0]
  },

  // Execute RPC function
  async rpc<T>(functionName: string, params?: Record<string, any>): Promise<T> {
    const response = await intelliJetNetAPI.post<T>(`/rpc/${functionName}`, params || {})
    return response.data
  },

  // Insert records
  async insert<T>(table: string, data: Partial<T> | Partial<T>[], options?: { returning?: string }): Promise<T[]> {
    const headers: any = { Prefer: options?.returning ? `return=representation` : 'return=minimal' }
    const response = await intelliJetNetAPI.post<T[]>(`/${table}`, data, { headers })
    return response.data
  },

  // Update records
  async update<T>(table: string, data: Partial<T>, filters: Filter[]): Promise<T[]> {
    const queryString = buildQueryString({ filters })
    const response = await intelliJetNetAPI.patch<T[]>(`/${table}?${queryString}`, data)
    return response.data
  },

  // Delete records
  async delete(table: string, filters: Filter[]): Promise<void> {
    const queryString = buildQueryString({ filters })
    await intelliJetNetAPI.delete(`/${table}?${queryString}`)
  },

  // Get with custom query string (legacy support)
  async getWithQuery<T>(table: string, queryString: string): Promise<T[]> {
    const response = await intelliJetNetAPI.get<T[]>(`/${table}?${queryString}`)
    return response.data
  }
}

export default intelliJetNetService